import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useCopy } from "../../context/CopyContext";

const SignInView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getCopy } = useCopy();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Get email from URL params if passed from QR code
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  const handleSignIn = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock sign in logic
    console.log("Sign in attempt:", { email, password, rememberMe });
    // For now, just show success
    alert("Sign in successful! (Mock)");
  };

  const handleSocialLogin = (provider: string) => {
    console.log(`${provider} login attempt`);
    alert(`${provider} login successful! (Mock)`);
  };

  const handleCreateAccount = () => {
    navigate(`/sign-up${email ? `?email=${encodeURIComponent(email)}` : ''}`);
  };

  return (
    <div className="min-h-screen bg-brand-background">
      {/* Mobile Container */}
      <div className="max-w-md mx-auto min-h-screen flex flex-col">
        {/* Header */}
        <div className="bg-black text-white p-4 text-center">
          <img 
            src="/brand-wob.png" 
            alt="Brand Logo" 
            className="h-8 mx-auto"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Sign In Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-2xl font-semibold mb-1">Sign In</h1>
              <p className="text-sm text-brand-text-secondary mb-6">
                required <span className="text-red-500">(*)</span>
              </p>

              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-text-primary mb-1">
                    Email address*
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-text-primary mb-1">
                    Password*
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-3 border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm pr-10 font-brand-secondary"
                      style={{ fontFamily: 'var(--brand-font-secondary)' }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showPassword ? (
                        <EyeOff size={16} className="text-brand-text-muted" />
                      ) : (
                        <Eye size={16} className="text-brand-text-muted" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm">Remember me</span>
                  </label>
                  <button
                    type="button"
                    className="text-sm underline hover:no-underline"
                  >
                    Forgot your password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="w-full bg-black text-white py-3 hover:bg-gray-800 transition-colors text-sm font-medium"
                >
                  SIGN IN
                </button>
              </form>

              <div className="mt-6">
                <p className="text-xs text-brand-text-secondary mb-4">
                  By signing in you are agreeing to our{" "}
                  <a href="#" className="underline">Terms of Use</a>,{" "}
                  <a href="#" className="underline">Privacy Policy</a> and{" "}
                  <a href="#" className="underline">Loyalty Terms</a>.
                </p>
              </div>

              <div className="mt-6">
                <p className="text-center text-sm font-medium mb-4">Or</p>
                <h3 className="text-lg font-semibold mb-2">Sign in with One Click</h3>
                <p className="text-sm text-brand-text-secondary mb-4">
                  Get logged in with your social media account below - it's that easy.
                </p>

                <div className="space-y-3">
                  <button
                    onClick={() => handleSocialLogin('Facebook')}
                    className="w-full bg-[#1877F2] text-white py-3 flex items-center justify-center space-x-2 hover:bg-[#166FE5] transition-colors"
                  >
                    <span className="text-sm font-medium">Facebook</span>
                  </button>
                  <button
                    onClick={() => handleSocialLogin('Google')}
                    className="w-full bg-[#4285F4] text-white py-3 flex items-center justify-center space-x-2 hover:bg-[#3367D6] transition-colors"
                  >
                    <span className="text-sm font-medium">Google</span>
                  </button>
                </div>

                <p className="text-xs text-brand-text-secondary mt-4">
                  When you use Facebook or Google to login to our site, be advised that your data is governed by 
                  Facebook's or Google's privacy policy and terms of use.
                </p>
              </div>
            </motion.div>

            {/* New Customer Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gray-50 p-6"
            >
              <h2 className="text-2xl font-semibold mb-4">New Customer</h2>
              <p className="text-sm text-brand-text-secondary mb-6">
                Create your account to enjoy exclusive benefits:
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-sm">Track your detailed orders</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-sm">Simple purchase for your favorite products</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-sm">Exclusive offers reserved for subscribers</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-sm">{getCopy('signIn.benefits')[3]}</span>
                </li>
              </ul>

              <button
                onClick={handleCreateAccount}
                className="w-full bg-black text-white py-3 hover:bg-gray-800 transition-colors text-sm font-medium"
              >
                CREATE MY ACCOUNT
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInView;
